import { useRef } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";

const PRODUCTS = [
    {
        slug: "ball-valves",
        index: "01",
        chip: "API 6D",
        title: "Ball Valves",
        specs: [
            'Trunnion & floating · sizes 2" – 48"',
            "Class 150 – 2500",
            "Fire-safe, zero-leakage metal seating",
        ],
        img: "/trunnion-ball-valve.jpg",
    },
    {
        slug: "gate-globe-check",
        index: "02",
        chip: "API 6A",
        title: "Gate, Globe & Check",
        specs: [
            "Wellhead & flowline service",
            "Up to 15,000 PSI",
            "NACE MR0175 sour-service options",
        ],
        img: "/gate-valves.jpg",
    },
    {
        slug: "butterfly-valves",
        index: "03",
        chip: "API 6D",
        title: "Butterfly Valves",
        specs: [
            "Triple-offset, bi-directional tight shut-off",
            "Cryogenic to high-temperature duty",
            "Wafer, lugged & flanged bodies",
        ],
        img: "/butterfly-valve.jpg",
    },
    {
        slug: "actuated-valves",
        index: "04",
        chip: "SIL 3",
        title: "Actuated Valves",
        specs: [
            "Pneumatic, hydraulic & electric packages",
            "ESD-ready, partial-stroke testing",
            "SIL-rated safety automation",
        ],
        img: "/actuated-valve.jpg",
    },
];

function ProductCard({ p, i }) {
    const ref = useRef(null);
    const rx = useSpring(useMotionValue(0), { stiffness: 150, damping: 18 });
    const ry = useSpring(useMotionValue(0), { stiffness: 150, damping: 18 });

    const onMove = (e) => {
        const r = ref.current.getBoundingClientRect();
        ry.set(((e.clientX - r.left) / r.width - 0.5) * 6);
        rx.set(-((e.clientY - r.top) / r.height - 0.5) * 6);
    };
    const onLeave = () => {
        rx.set(0);
        ry.set(0);
    };

    return (
        <motion.article
            data-testid={`product-card-${p.slug}`}
            initial={{ opacity: 0, y: 36 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7, delay: (i % 2) * 0.12 }}
            className="group h-full [perspective:1200px]"
        >
            <motion.div
                ref={ref}
                onMouseMove={onMove}
                onMouseLeave={onLeave}
                style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
                className="flex h-full flex-col border border-steel bg-obsidian-800 transition-colors duration-500 group-hover:border-tactical/60"
            >
                <div className="relative aspect-[16/10] overflow-hidden">
                    <img
                        src={p.img}
                        alt={p.title}
                        className="h-full w-full object-cover grayscale-[40%] transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-obsidian-800 via-transparent to-transparent" />
                    <span className="absolute left-4 top-4 font-mono text-xs tracking-[0.3em] text-slate-400">
                        /{p.index}
                    </span>
                    <span className="absolute right-4 top-4 border border-tactical/70 bg-obsidian/70 px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-tactical backdrop-blur-sm">
                        {p.chip}
                    </span>
                </div>
                <div className="flex flex-1 flex-col p-7">
                    <h3 className="font-display text-3xl font-bold uppercase tracking-tight text-slate-100">
                        {p.title}
                    </h3>
                    <ul className="mt-4 flex-1 space-y-2">
                        {p.specs.map((s) => (
                            <li
                                key={s}
                                className="flex gap-3 text-sm leading-relaxed text-slate-400"
                            >
                                <span className="mt-[7px] h-1 w-1 shrink-0 rotate-45 bg-tactical" />
                                {s}
                            </li>
                        ))}
                    </ul>
                    <button
                        data-testid={`product-card-${p.slug}-enquire-btn`}
                        onClick={() => window.__lenis?.scrollTo(document.getElementById("contact"), { offset: -60, duration: 1.4 }) || document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
                        className="mt-7 flex items-center gap-2 self-start border-b border-steel pb-1 font-mono text-[11px] font-bold uppercase tracking-[0.25em] text-slate-300 transition-colors duration-300 hover:border-tactical hover:text-tactical"
                    >
                        Enquire
                        <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
                    </button>
                </div>
            </motion.div>
        </motion.article>
    );
}

export default function Products() {
    return (
        <section id="products" data-testid="products-section" className="relative">
            <div className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:py-32">
                <div className="mb-14 flex flex-col justify-between gap-6 md:flex-row md:items-end">
                    <div>
                        <p className="mb-5 flex items-center gap-3 font-mono text-[11px] font-semibold uppercase tracking-[0.3em] text-tactical">
                            <span className="h-px w-10 bg-tactical" />
                            The Range
                        </p>
                        <h2 className="font-display text-5xl font-bold uppercase leading-[0.95] tracking-tight text-slate-100 sm:text-6xl">
                            Four families.
                            <br />
                            Every duty covered.
                        </h2>
                    </div>
                    <p className="max-w-sm text-sm leading-relaxed text-slate-400">
                        API 6A and API 6D compliant valve systems engineered
                        for drilling, production, and pipeline transmission
                        service worldwide.
                    </p>
                </div>
                <div className="grid gap-6 md:grid-cols-2">
                    {PRODUCTS.map((p, i) => (
                        <ProductCard key={p.slug} p={p} i={i} />
                    ))}
                </div>
            </div>
        </section>
    );
}
